import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Collapse,
  LinearProgress,
  Fade,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { 
  BandRecommendationFormData, 
  InstrumentWithPerformer,
  getActivityStatusInfo 
} from '../../types/BandRecommendations';
import BandStatusDisplay, { BandActivityStatus } from '../BandStatusDisplay';
import RichTextEditor from '../portfolio/RichTextEditor';
import InstrumentSelection from './InstrumentSelection';
import { SelectedInstrument } from '../../types/instrumentTypes';
import {
  Person,
  People,
  PhotoCamera,
  MusicNote,
  Info,
  Language,
  CheckCircle,
  ArrowDownward
} from '@mui/icons-material';
import FocalPointPicker from './FocalPointPicker';
import axios from 'axios';

interface BandInfoProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

const BandInfo: React.FC<BandInfoProps> = ({ formData, setFormData }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includePerformerNames, setIncludePerformerNames] = useState(true);

  const totalMembers = Number(formData.maleMembers || 0) + Number(formData.femaleMembers || 0);

  // Initialize performer names toggle based on existing data
  useEffect(() => {
    const hasPerformerNames = formData.instrumentation?.some(inst => 
      inst.performer && inst.performer.name && inst.performer.name.trim() !== ''
    );
    setIncludePerformerNames(hasPerformerNames || false);
  }, []);

  // Progressive disclosure checks - STRICT gating
  const hasName = Boolean(formData.artistName?.trim());
  const hasAbout = hasName && Boolean(formData.about?.trim());
  const hasWebsiteOrImage = hasAbout && Boolean(formData.performanceLink?.trim() || formData.mainImage);
  const hasComposition = hasWebsiteOrImage && totalMembers > 0;
  const hasInstrumentation = hasComposition && Boolean(formData.instrumentation?.length);

  // Check if user has entered data in later sections
  const hasLaterData = Boolean(
    formData.mainImage || 
    formData.performanceLink?.trim() ||
    totalMembers > 0 ||
    formData.instrumentation?.some(inst => inst.instrumentName) ||
    formData.activityStatus
  );

  // Each step only shows if the previous is complete OR if later data exists
  const showAbout = hasName || hasLaterData;
  const showWebsiteImage = hasAbout || hasLaterData;
  const showComposition = hasWebsiteOrImage || hasLaterData;
  const showInstrumentation = hasComposition || hasLaterData;
  const showStatus = hasInstrumentation || hasLaterData;

  // Progress calculation (simple bar, no percentages)
  const completedSteps = [hasName, hasAbout, hasWebsiteOrImage, hasComposition, hasInstrumentation].filter(Boolean).length;
  const progress = (completedSteps / 5) * 100;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    
    setFormData(prevData => {
      const newData = {
        ...prevData,
        [name]: value
      };

      // Auto-populate instrument slots when member count changes
      if (name === 'maleMembers' || name === 'femaleMembers') {
        const newTotal = Number(newData.maleMembers || 0) + Number(newData.femaleMembers || 0);
        
        // Always ensure we have exactly the right number of slots
        newData.instrumentation = Array(newTotal).fill(null).map((_, index) => {
          const existingInst = prevData.instrumentation?.[index];
          return existingInst || {
            instrumentId: `empty-slot-${index}`,
            instrumentName: '',
            withEffects: false,
            performer: { name: '' }
          };
        });
      }

      return newData;
    });
  };

  const handleActivityStatusChange = (event: any) => {
    const newStatus = event.target.value as BandActivityStatus;
    setFormData(prevData => ({
      ...prevData,
      activityStatus: newStatus,
      activityStatusDate: new Date()
    }));
  };

  const handleActivityNoteChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prevData => ({
      ...prevData,
      activityStatusNote: event.target.value
    }));
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploading(true);

    if (file.size > 50 * 1024 * 1024) {
      setError('File size exceeds 50MB limit');
      setUploading(false);
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      setError('Please upload a JPEG, PNG, or GIF file');
      setUploading(false);
      return;
    }

    const formDataPayload = new FormData();
    formDataPayload.append('image', file);

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload/band`, 
        formDataPayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          withCredentials: true,
        }
      );

      setFormData(prevData => ({
        ...prevData,
        mainImage: response.data.imageUrl,
      }));
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        'Failed to upload image. Please try again.'
      );

      if (error.response?.status === 401) {
        setError('Please log in to upload images');
      } else if (error.response?.status === 413) {
        setError('Image file is too large');
      }
    } finally {
      setUploading(false);
    }
  };

  // FIXED: Convert SelectedInstrument to your actual database format
  const handleInstrumentationChange = (index: number, instruments: SelectedInstrument[]) => {
    setFormData(prevData => {
      const newInstrumentation: InstrumentWithPerformer[] = [...(prevData.instrumentation || [])];
      
      // Ensure we have enough slots
      while (newInstrumentation.length <= index) {
        newInstrumentation.push({
          instrumentId: `empty-slot-${newInstrumentation.length}`,
          instrumentName: '',
          withEffects: false,
          performer: { name: '' }
        });
      }
      
      // Update the specific slot - PROPERLY CONVERT TO YOUR DATABASE FORMAT
      if (instruments.length > 0) {
        const inst = instruments[0]; // Each slot handles one instrument
        newInstrumentation[index] = {
          instrumentId: inst.id,
          instrumentName: inst.name,
          withEffects: inst.withEffects || false,
          performer: {
            name: (includePerformerNames && inst.performer?.name) ? inst.performer.name : ''
          }
        };
      } else {
        // Reset the slot if no instrument selected - BUT DON'T USE empty-slot placeholders
        newInstrumentation.splice(index, 1); // Remove this slot entirely
        
        // If we removed a slot, we need to adjust all subsequent slots
        for (let i = index; i < newInstrumentation.length; i++) {
          // Keep existing data but don't add empty slots
        }
      }

      // Filter out any empty slots to prevent database pollution
      const cleanInstrumentation = newInstrumentation.filter(inst => 
        inst.instrumentName && inst.instrumentName.trim() !== '' && 
        !inst.instrumentId.startsWith('empty-slot-')
      );

      return {
        ...prevData,
        instrumentation: cleanInstrumentation
      };
    });
  };

  // FIXED: Handle global performer names toggle properly
  const handlePerformerNamesToggle = (checked: boolean) => {
    setIncludePerformerNames(checked);
    
    // Update form data immediately - preserve existing names if toggling on
    setFormData(prevData => ({
      ...prevData,
      instrumentation: prevData.instrumentation?.map(inst => ({
        ...inst,
        performer: { 
          name: checked ? (inst.performer?.name || '') : ''
        }
      })) || []
    }));
  };

  const getNextStepHint = () => {
    if (!hasName) return "Start by telling us your band name";
    if (!hasAbout) return "Great! Now tell us about your band";
    if (!hasWebsiteOrImage) return "Add your website or upload an image";
    if (!hasComposition) return "How many members are in the band?";
    if (!hasInstrumentation) return "What instruments do you play?";
    return "Almost done! Just a few more details";
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            }
          }} 
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          {getNextStepHint()}
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Step 1: Band Name */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MusicNote color="primary" />
            <Typography variant="h6">Let's start with your band name</Typography>
            {hasName && <CheckCircle color="success" />}
          </Box>
          
          <TextField
            required
            name="artistName"
            label="Artist/Band Name"
            value={formData.artistName}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '1.1rem' } }}
            placeholder="Enter your band or artist name..."
            inputProps={{ autoComplete: 'off' }}
          />
        </CardContent>
      </Card>

      {/* Step 2: About (unlocked by name) */}
      <Collapse in={showAbout}>
        <Fade in={showAbout}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Info color="primary" />
                <Typography variant="h6">Tell us about {formData.artistName || 'your band'}</Typography>
                {hasAbout && <CheckCircle color="success" />}
                {hasName && !hasAbout && <ArrowDownward color="action" sx={{ ml: 'auto' }} />}
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  About the Artist/Band *
                </Typography>
                <RichTextEditor
                  value={formData.about || ''}
                  onChange={(html) => handleChange({ target: { name: 'about', value: html } } as any)}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Collapse>

      {/* Step 3: Website + Image (unlocked by about) */}
      <Collapse in={showWebsiteImage}>
        <Fade in={showWebsiteImage}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Language color="primary" />
                <Typography variant="h6">Website & Image</Typography>
                {hasWebsiteOrImage && <CheckCircle color="success" />}
                {hasAbout && !hasWebsiteOrImage && <ArrowDownward color="action" sx={{ ml: 'auto' }} />}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={formData.mainImage ? 6 : 12}>
                  <TextField
                    name="performanceLink"
                    label="Main Website"
                    value={formData.performanceLink}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    placeholder="https://your-website.com"
                    InputProps={{
                      startAdornment: <Language sx={{ color: 'action.active', mr: 1 }} />
                    }}
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Grid>

                <Grid item xs={12} md={formData.mainImage ? 6 : 12}>
                  <input
                    accept="image/jpeg,image/png,image/gif"
                    style={{ display: 'none' }}
                    id="main-image-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="main-image-upload">
                    <Button 
                      variant={formData.mainImage ? "outlined" : "contained"}
                      component="span" 
                      disabled={uploading}
                      fullWidth
                      startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCamera />}
                      sx={{ py: 1.5 }}
                    >
                      {uploading ? 'Uploading...' : formData.mainImage ? 'Change Image' : 'Upload Band Image'}
                    </Button>
                  </label>
                </Grid>

                {formData.mainImage && (
                  <Grid item xs={12}>
                    <FocalPointPicker
                      imageUrl={formData.mainImage}
                      value={formData.mainImagePosition || '50% 50%'}
                      onChange={(pos) => setFormData(prev => ({ ...prev, mainImagePosition: pos }))}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      </Collapse>

      {/* Step 4: Band Composition (unlocked by website/image) */}
      <Collapse in={showComposition}>
        <Fade in={showComposition}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People color="primary" />
                <Typography variant="h6">Band Composition</Typography>
                {hasComposition && <CheckCircle color="success" />}
                {hasWebsiteOrImage && !hasComposition && <ArrowDownward color="action" sx={{ ml: 'auto' }} />}
              </Box>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    required
                    name="maleMembers"
                    label="Male Members"
                    type="number"
                    InputProps={{ 
                      inputProps: { min: 0, max: 20 },
                      startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />
                    }}
                    value={formData.maleMembers}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    required
                    name="femaleMembers"
                    label="Female Members"
                    type="number"
                    InputProps={{ 
                      inputProps: { min: 0, max: 20 },
                      startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />
                    }}
                    value={formData.femaleMembers}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={4}>
                  <Box sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    border: '1px solid',
                    borderColor: hasComposition ? 'success.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.3s'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Members
                    </Typography>
                    <Typography variant="h4" color={hasComposition ? 'success.main' : 'primary'}>
                      {totalMembers}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      </Collapse>

      {/* Step 5: Instrumentation (unlocked by composition) */}
      <Collapse in={showInstrumentation}>
        <Fade in={showInstrumentation}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MusicNote color="primary" />
                <Typography variant="h6">Instrumentation</Typography>
                {hasInstrumentation && <CheckCircle color="success" />}
                {hasComposition && !hasInstrumentation && <ArrowDownward color="action" sx={{ ml: 'auto' }} />}
              </Box>
              
              {totalMembers > 0 && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Perfect! You have {totalMembers} members. Let's assign their instruments:
                </Typography>
              )}

              {/* Performer Names Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includePerformerNames}
                      onChange={(e) => handlePerformerNamesToggle(e.target.checked)}
                    />
                  }
                  label="Include performer names with instruments"
                />
              </Box>
              
              {/* Individual Instrument Slots */}
              {totalMembers > 0 && Array.from({ length: totalMembers }, (_, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Member {index + 1}
                  </Typography>
                  
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <InstrumentSelection
                      key={`member-${index}-${includePerformerNames}`}
                      value={formData.instrumentation?.[index] ? [{
                        id: formData.instrumentation[index].instrumentId,
                        name: formData.instrumentation[index].instrumentName,
                        withEffects: formData.instrumentation[index].withEffects,
                        performer: formData.instrumentation[index].performer
                      }] : []}
                      onChange={(instruments) => handleInstrumentationChange(index, instruments)}
                      showPerformerNames={includePerformerNames}
                    />
                  </Card>
                </Box>
              ))}

              {formData.instrumentation && formData.instrumentation.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Setup:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {formData.instrumentation.map((inst, index) => (
                      inst.instrumentName && (
                        <Chip 
                          key={index}
                          label={`${inst.instrumentName}${includePerformerNames && inst.performer?.name ? ` - ${inst.performer.name}` : ''}`}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                      )
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Collapse>

      {/* Step 6: Activity Status (unlocked by instrumentation) */}
      <Collapse in={showStatus}>
        <Fade in={showStatus}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Info color="primary" />
                <Typography variant="h6">Current Status</Typography>
                <CheckCircle color="success" />
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Finally, what's your current activity status?
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="activity-status-label">Activity Status</InputLabel>
                    <Select
                      labelId="activity-status-label"
                      id="activityStatus"
                      value={formData.activityStatus || BandActivityStatus.ACTIVE}
                      onChange={handleActivityStatusChange}
                      label="Activity Status"
                      sx={{
                        '.MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 1.5
                        }
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1
                        }}>
                          <BandStatusDisplay 
                            status={selected as BandActivityStatus} 
                            showLabel={true}
                            size={20}
                          />
                        </Box>
                      )}
                    >
                      {Object.values(BandActivityStatus).map((status) => {
                        const statusInfo = getActivityStatusInfo(status);
                        return (
                          <MenuItem 
                            key={status} 
                            value={status}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BandStatusDisplay 
                                status={status} 
                                showLabel={false}
                                size={20}
                              />
                              <Typography>{statusInfo.label}</Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="activityStatusNote"
                    label="Status Details"
                    value={formData.activityStatusNote || ''}
                    onChange={handleActivityNoteChange}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Additional context about current status..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      </Collapse>
    </Box>
  );
};

export default BandInfo;